import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { persistThemeSetting, THEME_DEFAULTS } from "../sing/utils/theme";
import type {
  LayoutContextValue,
  LayoutProviderProps,
  DashboardTheme,
  NavbarColor,
  NavbarType,
  SidebarType,
} from "../types/layout";

const LayoutContext = createContext<LayoutContextValue | null>(null);

function readStorage(key: string, fallback: string): string {
  try {
    const value = localStorage.getItem(key);
    return value != null ? value : fallback;
  } catch (e) {
    return fallback;
  }
}

export function LayoutProvider({ children }: LayoutProviderProps): React.ReactElement {
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [sidebarStatic, setSidebarStatic] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      return false;
    }
    return readStorage("staticSidebar", "true") === "true";
  });
  const [activeItem, setActiveItem] = useState<string | null>(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      return null;
    }
    return readStorage("staticSidebar", "true") === "true"
      ? window.location.pathname
      : null;
  });

  const [dashboardTheme, setDashboardTheme] = useState<DashboardTheme>(() =>
    readStorage("dashboardTheme", THEME_DEFAULTS.dashboardTheme)
  );
  const [navbarColor, setNavbarColor] = useState<NavbarColor>(() =>
    readStorage("navbarColor", THEME_DEFAULTS.navbarColor)
  );
  const [navbarType, setNavbarType] = useState<NavbarType>(() =>
    readStorage("navbarType", THEME_DEFAULTS.navbarType)
  );
  const [sidebarType, setSidebarType] = useState<SidebarType>(() =>
    readStorage("sidebarType", THEME_DEFAULTS.sidebarType)
  );

  const openSidebar = useCallback(() => setSidebarOpened(true), []);
  const closeSidebar = useCallback(() => setSidebarOpened(false), []);

  const toggleSidebarStatic = useCallback(() => {
    setSidebarStatic((prev) => {
      const next = !prev;
      localStorage.setItem("staticSidebar", String(next));
      return next;
    });
  }, []);

  const changeActiveSidebarItem = useCallback((item: string | null) => {
    setActiveItem(item);
  }, []);

  const changeTheme = useCallback((theme: DashboardTheme) => {
    persistThemeSetting("dashboardTheme", theme);
    setDashboardTheme(theme);
  }, []);

  const changeNavbarColor = useCallback((color: NavbarColor) => {
    persistThemeSetting("navbarColor", color);
    setNavbarColor(color);
  }, []);

  const navbarTypeToggle = useCallback((type: NavbarType) => {
    persistThemeSetting("navbarType", type);
    setNavbarType(type);
  }, []);

  const sidebarTypeToggle = useCallback((type: SidebarType) => {
    persistThemeSetting("sidebarType", type);
    setSidebarType(type);
  }, []);

  useEffect(() => {
    let wasMobile = typeof window !== "undefined" && window.innerWidth <= 768;

    const applyMobileShell = () => {
      setSidebarStatic(false);
      localStorage.setItem("staticSidebar", "false");
      setSidebarOpened(false);
      setActiveItem(null);
    };

    // Initial load on a phone/tablet width
    if (wasMobile) {
      applyMobileShell();
    }

    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      // Only react when crossing the breakpoint (not on every mobile chrome resize)
      if (isMobile && !wasMobile) {
        applyMobileShell();
      }
      wasMobile = isMobile;
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const value = useMemo<LayoutContextValue>(
    () => ({
      sidebarOpened,
      sidebarStatic,
      activeItem,
      dashboardTheme,
      navbarColor,
      navbarType,
      sidebarType,
      openSidebar,
      closeSidebar,
      toggleSidebarStatic,
      changeActiveSidebarItem,
      changeTheme,
      changeNavbarColor,
      navbarTypeToggle,
      sidebarTypeToggle,
      setSidebarOpened,
    }),
    [
      sidebarOpened,
      sidebarStatic,
      activeItem,
      dashboardTheme,
      navbarColor,
      navbarType,
      sidebarType,
      openSidebar,
      closeSidebar,
      toggleSidebarStatic,
      changeActiveSidebarItem,
      changeTheme,
      changeNavbarColor,
      navbarTypeToggle,
      sidebarTypeToggle,
    ]
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

export function useLayout(): LayoutContextValue {
  const ctx = useContext(LayoutContext);
  if (!ctx) {
    throw new Error("useLayout must be used within LayoutProvider");
  }
  return ctx;
}
